import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    StoreAdminDashboard,
    AccountingSummary, InventorySummary, DashboardLayout,
    SalesSummary,
    KpiTarget, KpiTargets,
    ArAgingBucket, TopReceivable, MonthlyIncomeExpense, AccountAmount, StoreValue, WeeklyMovement, ReorderRow, TopItem,
    PipelineStage, MonthlyAmount, QuotationOutcome, TopDeal,
} from '../interfaces/dashboard.interface';

// Every dashboard figure is "as of today", and the server runs in UTC — so the
// caller's zone has to travel with the request or a UTC+6 tenant is shown the
// previous day's numbers between midnight and 06:00 local.
//
// AuthContext also sets axios.defaults.headers.common['X-Timezone'], but that
// cannot be relied on here: it is set in the provider's useEffect, and React runs
// child effects BEFORE parent effects — so the dashboard's own fetch-on-mount
// races ahead of it and goes out bare. Send it explicitly per request, the same
// way attendance.service.ts does.
const tzHeader = () => ({
    ...AuthService.getAuthHeader(),
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
});

export const DashboardService = {
    getStoreAdminDashboard: async (institutionId: string): Promise<StoreAdminDashboard> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.STORE_ADMIN(institutionId), {
            headers: tzHeader()
        });
        return response.data;
    },

    getAccountingSummary: async (institutionId: string): Promise<AccountingSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getInventorySummary: async (institutionId: string): Promise<InventorySummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getSalesSummary: async (institutionId: string): Promise<SalesSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getLayout: async (institutionId: string, boardKey = 'home'): Promise<DashboardLayout> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.LAYOUT(institutionId, boardKey), { headers: tzHeader() });
        return response.data;
    },

    saveLayout: async (institutionId: string, boardKey: string, layout: DashboardLayout): Promise<DashboardLayout> => {
        const response = await axios.put(ENDPOINTS.DASHBOARD.LAYOUT(institutionId, boardKey), layout, { headers: tzHeader() });
        return response.data;
    },

    getKpiTargets: async (institutionId: string): Promise<KpiTargets> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.KPI_TARGETS(institutionId), { headers: tzHeader() });
        return response.data;
    },

    setKpiTargets: async (institutionId: string, targets: KpiTarget[]): Promise<KpiTargets> => {
        const response = await axios.put(ENDPOINTS.DASHBOARD.KPI_TARGETS(institutionId), { targets }, { headers: tzHeader() });
        return response.data;
    },

    getAccountingArAging: async (institutionId: string): Promise<ArAgingBucket[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_AR_AGING(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getAccountingTopReceivables: async (institutionId: string, limit = 8): Promise<TopReceivable[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_TOP_RECEIVABLES(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getAccountingTrend: async (institutionId: string, months = 6): Promise<MonthlyIncomeExpense[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_TREND(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },

    getAccountingExpenseByAccount: async (institutionId: string): Promise<AccountAmount[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_EXPENSE_BY_ACCOUNT(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getInventoryValueByStore: async (institutionId: string): Promise<StoreValue[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_VALUE_BY_STORE(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getInventoryMovement: async (institutionId: string, weeks = 8): Promise<WeeklyMovement[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_MOVEMENT(institutionId), {
            params: { weeks }, headers: tzHeader(),
        });
        return response.data;
    },

    getInventoryReorder: async (institutionId: string, limit = 10): Promise<ReorderRow[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_REORDER(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getInventoryTopItems: async (institutionId: string, limit = 8): Promise<TopItem[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_TOP_ITEMS(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getSalesPipeline: async (institutionId: string): Promise<PipelineStage[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_PIPELINE(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getSalesRevenue: async (institutionId: string, months = 6): Promise<MonthlyAmount[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_REVENUE(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },

    getSalesOutcomes: async (institutionId: string, days = 90): Promise<QuotationOutcome[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_OUTCOMES(institutionId), {
            params: { days }, headers: tzHeader(),
        });
        return response.data;
    },

    getSalesTopDeals: async (institutionId: string, limit = 8): Promise<TopDeal[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_TOP_DEALS(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

};
